// Hooks for DocumentExemplars — a customer's uploaded past reports, from which a reusable document
// blueprint is extracted (mirrors use-policy-documents.js).
import { useContext } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { apiGet, apiDelete, apiPost } from 'api'
import { baseUrl } from 'api/helpers'
import AuthContext from 'context/auth-context'
import UserContext from 'context/user-context'
import axios from 'axios'

const getDocumentExemplars = async (token, municipalityId) => {
  const url = `municipalities/${municipalityId}/document_exemplars`
  const data = await apiGet({ url, token })
  return data
}

export const useDocumentExemplars = ({ options = {} } = {}) => {
  const { municipality_id } = useContext(UserContext)
  const token = useContext(AuthContext)

  const queryFn = () => getDocumentExemplars(token, municipality_id)
  const queryKey = ['document-exemplars', municipality_id]

  return useQuery(queryKey, queryFn, { ...options })
}

const uploadDocumentExemplar = async (token, municipalityId, { name, sectionId, file }) => {
  const url = `${baseUrl}/municipalities/${municipalityId}/document_exemplars`
  const formData = new FormData()
  formData.append('document_exemplar[document]', file)
  formData.append('document_exemplar[name]', name)
  if (sectionId) formData.append('document_exemplar[section_id]', sectionId)

  const response = await axios.post(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
  })
  return response.data
}

export const useUploadDocumentExemplar = ({ options = {} } = {}) => {
  const { municipality_id } = useContext(UserContext)
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()
  const { onSuccess: externalOnSuccess, ...rest } = options

  return useMutation((params) => uploadDocumentExemplar(token, municipality_id, params), {
    onSuccess: (...args) => {
      queryClient.invalidateQueries(['document-exemplars', municipality_id])
      externalOnSuccess?.(...args)
    },
    ...rest
  })
}

const completeDocumentExemplar = async (token, municipalityId, exemplarId) => {
  const url = `municipalities/${municipalityId}/document_exemplars/${exemplarId}/complete`
  return apiPost({ url, token, params: {} })
}

export const useCompleteDocumentExemplar = ({ options = {} } = {}) => {
  const { municipality_id } = useContext(UserContext)
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()
  const { onSuccess: externalOnSuccess, ...rest } = options

  return useMutation((exemplarId) => completeDocumentExemplar(token, municipality_id, exemplarId), {
    onSuccess: (...args) => {
      queryClient.invalidateQueries(['document-exemplars', municipality_id])
      externalOnSuccess?.(...args)
    },
    ...rest
  })
}

const extractDocumentExemplar = async (token, municipalityId, exemplarId) => {
  const url = `municipalities/${municipalityId}/document_exemplars/${exemplarId}/extract`
  return apiPost({ url, token, params: {} })
}

// Retry blueprint extraction on a stuck/failed exemplar.
export const useExtractDocumentExemplar = ({ options = {} } = {}) => {
  const { municipality_id } = useContext(UserContext)
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()
  const { onSuccess: externalOnSuccess, ...rest } = options

  return useMutation((exemplarId) => extractDocumentExemplar(token, municipality_id, exemplarId), {
    onSuccess: (...args) => {
      queryClient.invalidateQueries(['document-exemplars', municipality_id])
      externalOnSuccess?.(...args)
    },
    ...rest
  })
}

const destroyDocumentExemplar = async (token, municipalityId, exemplarId) => {
  const url = `municipalities/${municipalityId}/document_exemplars/${exemplarId}`
  return apiDelete({ url, token })
}

export const useDestroyDocumentExemplar = ({ options = {} } = {}) => {
  const { municipality_id } = useContext(UserContext)
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()
  const { onSuccess: externalOnSuccess, ...rest } = options

  return useMutation((exemplarId) => destroyDocumentExemplar(token, municipality_id, exemplarId), {
    onSuccess: (...args) => {
      queryClient.invalidateQueries(['document-exemplars', municipality_id])
      externalOnSuccess?.(...args)
    },
    ...rest
  })
}
